// src/components/Common/Notice/Notice.jsx
import React from 'react';
import { FaCircleInfo, FaTriangleExclamation, FaCircleCheck, FaCircleXmark } from 'react-icons/fa6';
import './Notice.less';

const DEFAULT_ICONS = {
  info: FaCircleInfo,
  warning: FaTriangleExclamation,
  success: FaCircleCheck,
  danger: FaCircleXmark,
  // Light is a neutral helper-card variant. Same info glyph by default,
  // but rendered in muted slate (token-driven) so it doesn't read as a
  // typed alert. Callers can pass `icon={false}` to omit it entirely.
  light: FaCircleInfo,
  // Solid variants share the glyph with their pastel counterpart but
  // render on a saturated background with white text. Used for the
  // high-attention charging states.
  'warning-solid': FaTriangleExclamation,
  'danger-solid': FaCircleXmark,
};

const ARIA_ROLES = {
  info: 'status',
  success: 'status',
  warning: 'alert',
  danger: 'alert',
  light: 'status', // informational, non-critical
  'warning-solid': 'alert',
  'danger-solid': 'alert',
};

/**
 * Notice — compact, mobile-native helper card for in-app guidance.
 *
 * One reusable shape for pre-authorization holds, payment/release
 * explanations, charging guidance, warning banners, and helper notes.
 * Visual tokens live in App.less (`--notice-*`) so callers never set
 * font-size or padding directly — that's how we keep notices from
 * drifting back to "too large / too heavy."
 *
 * Props:
 *   variant   info | warning | success | danger (default: info)
 *   title     optional bolded title line
 *   icon      ReactNode to override; pass `false` to hide entirely
 *   actions   optional ReactNode rendered below the body (e.g. buttons)
 *   children  body content
 *   role      optional ARIA role override
 */
const Notice = ({
  variant = 'info',
  title = null,
  icon,
  actions = null,
  className = '',
  role,
  children,
}) => {
  const safeVariant = DEFAULT_ICONS[variant] ? variant : 'info';
  const IconComp = DEFAULT_ICONS[safeVariant];

  const renderIcon = () => {
    if (icon === false) return null;
    if (React.isValidElement(icon)) {
      return <span className="notice__icon">{icon}</span>;
    }
    return (
      <span className="notice__icon">
        <IconComp aria-hidden="true" />
      </span>
    );
  };

  return (
    <div
      className={`notice notice--${safeVariant} ${className}`.trim()}
      role={role || ARIA_ROLES[safeVariant]}
    >
      {renderIcon()}
      <div className="notice__content">
        {title && <p className="notice__title">{title}</p>}
        {children && <div className="notice__body">{children}</div>}
        {actions && <div className="notice__actions">{actions}</div>}
      </div>
    </div>
  );
};

export default Notice;
